import SailboatOffshore16 from '@carbon/icons-react/es/sailboat--offshore/16';
import { Table, Tooltip } from 'antd';
import { Moment } from 'moment';
import React, { FC } from 'react';
import styled from 'styled-components';
import { User } from 'api/models';
import TimeOffTriangle from 'assets/svgs/timeoff-triangle.svg';
import { BodyShort01, Caption01, ProductiveHeading07 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { checkIfCurrentDateHasTimeOff } from 'utils/time-off-utils';

const SecondaryHeaderRow = styled.div`
  ${Caption01};
  color: var(--gray-70);
`;
const Wrapper = styled.div`
  ${ProductiveHeading07};
  text-align: left;
  flex-grow: 1;
  justify-items: flex-end;
`;

const HeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;

  svg {
    color: var(--purple-60);
  }
`;

const TimeOffWrapper = styled.span`
  position: absolute;
  top: var(--spacing-01);
  right: 0;
`;

export const DateColumnHeaderCell: FC<{ date: Moment; user?: User }> = ({
  date,
  user,
}) => {
  let isTimeOff;

  if (date.day() !== 0 && date.day() !== 6) {
    isTimeOff = checkIfCurrentDateHasTimeOff(user?.userTimeOffDetails, date);
  }
  const timeOffText = useTranslation('settings.profile.timeoff.title');

  return (
    <HeadingWrapper>
      <Wrapper>
        <div>{date.format('ddd')}</div>
        <SecondaryHeaderRow>{date.format('DD MMM')}</SecondaryHeaderRow>
      </Wrapper>
      {!!isTimeOff && (
        <TimeOffWrapper>
          <Tooltip title={timeOffText}>
            <SailboatOffshore16 />
          </Tooltip>
        </TimeOffWrapper>
      )}
    </HeadingWrapper>
  );
};

export const BaseCell = styled.div`
  ${BodyShort01};
  padding: var(--spacing-03) var(--spacing-05);
  height: var(--spacing-09);
  display: flex;
  align-items: center;
`;

export const TimesheetBaseTable = styled(Table)`
  .ant-table-thead > tr > th {
    background: var(--coolest-gray-20);
    ${ProductiveHeading07};
    padding: 0 var(--spacing-05);
    height: var(--height-base);
  }

  .ant-table-column-sorters {
    padding: 0;
    max-width: 100%;
    vertical-align: text-bottom;

    > span:first-child {
      width: 90%;
    }
  }

  .ant-table-column-sorter-full {
    margin-top: -4px;
    margin-bottom: -0.15em;
  }

  .ant-table-body {
    overflow: auto !important;
    overflow-x: hidden !important;
  }

  .ant-table-cell-scrollbar {
    box-shadow: 0 1px 0 1px var(--coolest-gray-20);
  }

  .ant-table-tbody > tr > td {
    padding: 0;
    vertical-align: top;
  }
`;

export const TimesheetTable = styled(TimesheetBaseTable)`
  .ant-table-thead > tr > th {
    padding: calc(calc(1 * var(--spacing-03)) - 1px) var(--spacing-04);
  }

  .ant-table-thead > tr > th.active-column {
    background: var(--coolest-gray-30);
  }

  .ant-table-thead > tr > th.holiday-cell {
    opacity: 0.7;
  }

  .ant-table-tbody > tr > td.holiday-cell {
    background: var(--coolest-gray-30);
    background: repeating-linear-gradient(
      -47deg,
      #fff 1%,
      #fff 2%,
      var(--coolest-gray-30) 3%,
      #fff 4%,
      #fff 5%
    );
  }

  .ant-table-tbody > tr > td.timeoff-cell::before {
    content: '';
    background-image: url(${TimeOffTriangle});
    background-repeat: repeat;
    opacity: 0.15;
    position: absolute;
    inset: 0;
  }

  .ant-table-tbody > tr > td.today-cell {
    background: var(--gray-10);
  }

  .in-active > td:not(:last-child) {
    opacity: 0.4;
    cursor: disabled;
  }

  .in-active > td:first-child div,
  .in-active > td:nth-child(2) div {
    text-decoration: line-through;
  }

  .ant-table-tbody > tr > td.holiday-cell.today-cell {
    background: var(--coolest-gray-30);
    background: repeating-linear-gradient(
      -47deg,
      var(--gray-10) 1%,
      var(--gray-10) 2%,
      var(--coolest-gray-30) 3%,
      var(--gray-10) 4%,
      var(--gray-10) 5%
    );
  }
`;
export const FooterRow = styled(Table)`
  .ant-table-thead > tr > th {
    background: var(--coolest-gray-20);
    ${ProductiveHeading07};
    padding: calc(calc(1 * var(--spacing-03)) - 1px) var(--spacing-04);
    padding-right: var(--spacing-02);
  }

  .ant-table-tbody {
    display: none;
  }
`;
